import { useCallback, useState } from 'react'
import { useAppDispatch } from 'common/hooks/redux'
import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { procedureApi, useUpdateProcedureMutation } from 'features/Procedure/state/procedure.api'
import type { ProcedureItem, SoapNoteApiItem } from 'features/Procedure/state/procedure-api.type'

let nextTempImageId = -1000

const getNextTempImageId = () => {
  const id = nextTempImageId
  nextTempImageId -= 1
  return id
}

type UploadArgs = {
  procedure: ProcedureItem
  imageDataUrl: string
}

type Props = {
  siteId: string | undefined
  selectedPatientId: number | null
  selectedEncounterUid: string | null
}

export const useUploadProcedureSoapImage = ({
  siteId,
  selectedPatientId,
  selectedEncounterUid,
}: Props) => {
  const dispatch = useAppDispatch()
  const { setNotification } = useNotification()
  const [isUploading, setIsUploading] = useState(false)
  const [updateProcedure] = useUpdateProcedureMutation()

  const canUpload = Boolean(siteId && selectedPatientId && selectedEncounterUid)

  const uploadImage = useCallback(
    async ({ procedure, imageDataUrl }: UploadArgs) => {
      if (!siteId || !selectedPatientId || !selectedEncounterUid) {
        return
      }

      setIsUploading(true)

      const tempId = getNextTempImageId()
      const queryArg = { patientPid: selectedPatientId, encounterUid: selectedEncounterUid }

      const patchResult = (dispatch as any)(
        procedureApi.util.updateQueryData(
          'fetchSoapNotes',
          queryArg,
          (draft: SoapNoteApiItem[]) => {
            if (draft.length === 0) {
              const optimisticNote: SoapNoteApiItem = {
                id: tempId - 1,
                date: new Date().toISOString(),
                pid: selectedPatientId,
                user: null,
                groupname: null,
                authorized: 1,
                activity: 1,
                subjective: null,
                objective: null,
                assessment: null,
                plan: null,
                soap_images: [],
              }
              draft.unshift(optimisticNote)
            }

            const note = draft[0]
            if (!Array.isArray(note.soap_images)) {
              note.soap_images = []
            }
            note.soap_images.push({
              id: tempId,
              image: imageDataUrl,
              procedure_id: procedure.id,
            })
          },
        ),
      )

      try {
        await updateProcedure({
          siteId,
          encounterUid: selectedEncounterUid,
          data: {
            procedures: [
              {
                id: procedure.id,
                code: procedure.code,
                code_type: procedure.code_type,
                procedure_units: procedure.procedure_units,
                units: procedure.units,
                fee: procedure.fee,
                provider_id: procedure.provider_id,
                justify_codes: procedure.justify_codes,
                image: imageDataUrl,
              },
            ],
          },
        }).unwrap()
      } catch (error: any) {
        patchResult.undo()
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Unable to upload image',
          description: error?.data?.message || 'Please try again later.',
        })
      } finally {
        setIsUploading(false)
      }
    },
    [siteId, selectedPatientId, selectedEncounterUid, dispatch, updateProcedure, setNotification],
  )

  return {
    uploadImage,
    isUploading,
    canUpload,
  }
}
